import { Card, DatePicker, Spin, Table, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Bar, Column, Line } from "@ant-design/plots";
import { Dayjs } from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  getCompanies,
  getCompanyDepartments,
  getDashboardRiskCompanies,
  getDashboardRiskDomains,
  getDashboardRiskTrend,
  getDashboardRisks,
  getQuestionnaires,
} from "../../repository/data.repository";
import {
  DashboardRiskCompany,
  DashboardRiskDomain,
  DashboardRiskDomains,
  DashboardRiskTrend,
  DashboardRiskTrendPoint,
  DashboardRisks,
} from "../../repository/data.default";
import {
  createScopedSearchRequest,
  createSearchRequest,
} from "../../hooks/useSearchRequest";
import { pickBucket, resolveRange } from "./AnalyticsRange";
import {
  ALARM_RED,
  MetricChartEmpty,
  OK_GREEN,
  WARN_ORANGE,
} from "./MetricChartRender";
import { InsightStrip } from "./InsightCards";
import RemoteSelect from "./RemoteSelect";
import { useServerPagedTable } from "./useServerPagedTable";

// Fallback palette for bands that carry no colour of their own (riskiest-first, so it opens on
// the shared alarm tokens).
const FALLBACK_BAND_COLORS = [
  ALARM_RED,
  WARN_ORANGE,
  "#fadb14",
  "#a0d911",
  OK_GREEN,
  "#13c2c2",
  "#1677ff",
  "#722ed1",
];

const questionnaireSearch = createSearchRequest(getQuestionnaires, "name");
const companySearch = createSearchRequest(getCompanies, "name");

/** Page size of the companies-at-risk table. */
const COMPANIES_PAGE_SIZE = 8;

// Platform risk overview: scored KPI results rolled up server-side into a band distribution plus
// a worst-first companies-at-risk table. Carries its own filter bar (questionnaire · company ·
// department · date range); everything is optional, so the default view is platform-wide.
function RisksCard() {
  const [companyUid, setCompanyUid] = useState<string | undefined>();
  const [departmentUid, setDepartmentUid] = useState<string | undefined>();
  const [questionnaireUid, setQuestionnaireUid] = useState<string | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [risks, setRisks] = useState<DashboardRisks | undefined>();
  const [domains, setDomains] = useState<DashboardRiskDomains | undefined>();
  const [trend, setTrend] = useState<DashboardRiskTrend | undefined>();

  // Departments cascade off the chosen company: the scoped request reads the company live (via a
  // ref, so the loader keeps a stable identity) and RemoteSelect refetches on open.
  const companyUidRef = useRef<string | undefined>(undefined);
  companyUidRef.current = companyUid;
  const departmentSearch = useMemo(
    () =>
      createScopedSearchRequest(
        getCompanyDepartments,
        "name",
        "companyUid",
        () => companyUidRef.current,
      ),
    [],
  );

  // Resolved start-of-day / end-of-day bounds, or undefined when no range is picked.
  const resolvedRange: [string, string] | undefined =
    range && range[0] && range[1]
      ? (() => {
          const r = resolveRange(
            {
              mode: "custom",
              custom: {
                start: range[0].format("YYYY-MM-DD"),
                end: range[1].format("YYYY-MM-DD"),
              },
            },
            [],
          );
          return r.start && r.end ? [r.start, r.end] : undefined;
        })()
      : undefined;

  const from = resolvedRange?.[0];
  const to = resolvedRange?.[1];

  // The trend's granularity follows the span, as every other dated card does: long spans and
  // all-time roll up to months, medium to weeks, short stay daily. Without this the series is
  // permanently daily, which over a year is 365 points nobody can read.
  const bucket = pickBucket(from, to);

  // Refetch the rollup on any filter change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    // The three rollups describe the same population, so they load and fail together — a card that
    // showed a distribution beside a stale trend would be worse than one that showed neither.
    const scope = { questionnaireUid, companyUid, departmentUid, from, to };
    Promise.all([
      getDashboardRisks(scope),
      getDashboardRiskDomains(scope),
      getDashboardRiskTrend({ ...scope, bucket }),
    ])
      .then(([rollup, domainRollup, trendRollup]) => {
        if (cancelled) return;
        setRisks(rollup);
        setDomains(domainRollup);
        setTrend(trendRollup);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [questionnaireUid, companyUid, departmentUid, from, to, bucket]);

  // The companies-at-risk table pages on the server; any filter change re-ranks it, so it
  // starts back at the first (worst) page.
  const { rows, totalRecords, tableLoading, pagination } =
    useServerPagedTable<DashboardRiskCompany>(
      (page) =>
        getDashboardRiskCompanies({
          questionnaireUid,
          companyUid,
          departmentUid,
          from,
          to,
          pageNumber: page,
          pageSize: COMPANIES_PAGE_SIZE,
        }),
      [questionnaireUid, companyUid, departmentUid, from, to],
      COMPANIES_PAGE_SIZE,
    );

  // Bands riskiest-first (ordinal ascending), each coloured by its own band colour when set.
  const bands = [...(risks?.bandDistribution ?? [])].sort(
    (a, b) => a.ordinal - b.ordinal,
  );
  const bandColors = bands.map(
    (b, i) => b.bandColor || FALLBACK_BAND_COLORS[i % FALLBACK_BAND_COLORS.length],
  );

  // Domains arrive worst-first from the server; only those with at least one high-risk result are
  // worth charting — a domain with none is not a recurring risk.
  const domainRows = (domains?.domains ?? []).filter((d) => d.highRiskCount > 0);
  const trendRows = trend?.series ?? [];

  const awaitingScoring = risks?.awaitingScoring ?? 0;

  const companyColumns: ColumnsType<DashboardRiskCompany> = [
    { title: "Company", dataIndex: "companyName" },
    { title: "Results", dataIndex: "totalResults", align: "right" },
    { title: "High-risk", dataIndex: "highRiskCount", align: "right" },
    {
      title: "Share",
      dataIndex: "highRiskShare",
      align: "right",
      render: (share: number) =>
        share > 0 ? (
          <Tag color="red">{`${Math.round(share * 10) / 10}%`}</Tag>
        ) : (
          <Typography.Text type="secondary">0%</Typography.Text>
        ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Typography.Text type="secondary" className="text-xs">
            Questionnaire
          </Typography.Text>
          <RemoteSelect
            className="w-64"
            placeholder="All questionnaires"
            value={questionnaireUid}
            onChange={setQuestionnaireUid}
            request={questionnaireSearch}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Typography.Text type="secondary" className="text-xs">
            Company
          </Typography.Text>
          <RemoteSelect
            className="w-56"
            placeholder="All companies"
            value={companyUid}
            onChange={(v) => {
              setCompanyUid(v);
              // Cascade: a department belongs to one company — clear it when the company changes.
              setDepartmentUid(undefined);
            }}
            request={companySearch}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Typography.Text type="secondary" className="text-xs">
            Department
          </Typography.Text>
          <RemoteSelect
            className="w-52"
            placeholder="All departments"
            disabled={!companyUid}
            value={departmentUid}
            onChange={setDepartmentUid}
            request={departmentSearch}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Typography.Text type="secondary" className="text-xs">
            Date range
          </Typography.Text>
          <DatePicker.RangePicker
            value={range}
            onChange={(v) => setRange(v as [Dayjs, Dayjs] | null)}
            allowClear
          />
        </div>
      </div>

      <InsightStrip
        items={[
          `${(risks?.totalResults ?? 0).toLocaleString()} results`,
          `${(risks?.respondentCount ?? 0).toLocaleString()} respondents`,
          awaitingScoring > 0 ? (
            <Tooltip title="Completed answers not yet scored — their results aren't on this card yet">
              <Typography.Text type="warning">
                {awaitingScoring.toLocaleString()} awaiting scoring
              </Typography.Text>
            </Tooltip>
          ) : (
            "0 awaiting scoring"
          ),
        ]}
      />

      <div className="flex flex-row flex-wrap gap-3">
        <div className="min-w-[340px] flex-1">
          <Card title="Band distribution" className="flex-auto">
            <Spin spinning={loading}>
              {error ? (
                <MetricChartEmpty label="Something went wrong" />
              ) : bands.length ? (
                <Column
                  data={bands}
                  xField="bandLabel"
                  yField="count"
                  colorField="bandLabel"
                  height={280}
                  tooltip={false}
                  legend={false}
                  scale={{
                    x: { padding: 0.5 },
                    color: {
                      domain: bands.map((b) => b.bandLabel),
                      range: bandColors,
                    },
                  }}
                  axis={{
                    x: { line: true },
                    y: {
                      line: true,
                      title: "Results",
                      tickFilter: (d: any) => Number.isInteger(d),
                    },
                  }}
                />
              ) : (
                <MetricChartEmpty />
              )}
            </Spin>
          </Card>
        </div>
        <div className="min-w-[340px] flex-1">
          <Card title="Companies at risk" className="flex-auto">
            {error ? (
              <MetricChartEmpty label="Something went wrong" />
            ) : (
              <div className="flex flex-col gap-2">
                <Typography.Text type="secondary" className="text-xs">
                  Results landing in the lowest band of each KPI's scale are counted as
                  high-risk.
                </Typography.Text>
                <Table
                  size="small"
                  rowKey="companyUid"
                  columns={companyColumns}
                  dataSource={rows}
                  loading={tableLoading}
                  pagination={pagination}
                />
                <Typography.Text type="secondary" className="text-xs">
                  {totalRecords.toLocaleString()} companies
                </Typography.Text>
              </div>
            )}
          </Card>
        </div>
      </div>

      <div className="flex flex-row flex-wrap gap-3">
        <div className="min-w-[340px] flex-1">
          <Card title="Most common risk domains" className="flex-auto">
            <Spin spinning={loading}>
              {error ? (
                <MetricChartEmpty label="Something went wrong" />
              ) : domainRows.length ? (
                <div className="flex flex-col gap-2">
                  {/* Bar takes the CATEGORY as xField and the measure as yField, as the shared
                      metric charts do — reversing them puts the domain names on the value axis.
                      The measure is SHARE, not count: sizing by count ranks domains by how many
                      results they carry rather than how concentrated their risk is, which buries the
                      ones worth acting on. Each bar is labelled with the counts behind it, because a
                      high share over few results is a weak signal and the reader needs to see that. */}
                  <Bar
                    data={domainRows}
                    xField="domain"
                    yField="highRiskShare"
                    // Tall enough to name every domain. At a fixed 280 the axis silently drops
                    // roughly half its labels, leaving bars nobody can identify — and a risk domain
                    // you cannot name is not actionable.
                    height={Math.max(280, domainRows.length * 26 + 60)}
                    legend={false}
                    scale={{ x: { domain: domainRows.map((d) => d.domain) } }}
                    label={{
                      text: (d: DashboardRiskDomain) =>
                        `${d.highRiskCount} of ${d.totalResults}`,
                      // "right" sits the label INSIDE the bar's right edge, so it needs to read
                      // against the fill, not against the card.
                      position: "right",
                      dx: -6,
                      style: { fontSize: 11, fill: "#fff", textAlign: "end" },
                    }}
                    axis={{
                      x: { line: true },
                      y: { line: true, title: "High-risk share (%)" },
                    }}
                  />
                  {/* A KPI drawing on several sections has no single domain, so its results are
                      reported rather than silently folded into one of them. */}
                  {domains?.unattributedResults ? (
                    <Typography.Text type="secondary" className="text-xs">
                      {domains.unattributedResults.toLocaleString()} results come from KPIs
                      spanning several sections and aren't attributed to a domain.
                    </Typography.Text>
                  ) : null}
                </div>
              ) : (
                <MetricChartEmpty />
              )}
            </Spin>
          </Card>
        </div>
        <div className="min-w-[340px] flex-1">
          <Card title="Risk trend over time" className="flex-auto">
            <Spin spinning={loading}>
              {error ? (
                <MetricChartEmpty label="Something went wrong" />
              ) : trendRows.length ? (
                <div className="flex flex-col gap-2">
                  {/* Points, not just the line: early in a company's life the series is one or two
                      buckets, and a line through a single point draws nothing at all.
                      Each point carries the result count behind it — a share says nothing without
                      its denominator, and 100% of two results must not read like 45% of twelve
                      hundred. */}
                  <Line
                    data={trendRows}
                    xField="date"
                    yField="highRiskShare"
                    height={280}
                    legend={false}
                    point={{ shapeField: "square", sizeField: 4 }}
                    label={{
                      text: (d: DashboardRiskTrendPoint) => `${d.totalResults}`,
                      position: "top",
                      // Lifted clear of the point marker. Sitting on it, the marker covered the
                      // leading digit and 240 results read as 40 — a wrong number, which is worse
                      // than none.
                      dy: -10,
                      textAlign: "center",
                      style: { fontSize: 11, fill: "#8c8c8c" },
                    }}
                    scale={{ x: { padding: 0.5 } }}
                    axis={{
                      x: { line: true, label: true },
                      y: { line: true, title: "High-risk share (%)" },
                    }}
                  />
                  <Typography.Text type="secondary" className="text-xs">
                    Share of results in the lowest band of their KPI's scale, by{" "}
                    {trend?.bucket ?? "day"}.
                  </Typography.Text>
                </div>
              ) : (
                <MetricChartEmpty />
              )}
            </Spin>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default RisksCard;
